import { Button, ConfigProvider, DatePicker, Form, message, Modal, Popconfirm, Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import { ExclamationCircleFilled } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { DEF_MONTH_FORMAT, GlobalUtils } from "@/app/home/utils/global.utils";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { WaterInvoiceApproveStatus } from "@/core/enums/global.enums";
import { waterInvoiceApproveStatusPipe } from "@/core/pipes/invoice-approve-status.pipe";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);
const tollAreaRepo = container.get(TollAreaRepo);

export interface _ApproveByTollAreaQuery {
    date: Dayjs,
    tollAreaId: string,
    approveStatus: string
}

export interface ApproveByTollAreaQuery {
    tollAreaId?: string,
    month: string,
    year: string,
}


interface ApproveByTollAreaModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: ApproveByTollAreaQuery
}


const INPUT_WIDTH = 450;

export const ApproveByTollAreaModal: React.FC<ApproveByTollAreaModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const [form] = Form.useForm<_ApproveByTollAreaQuery>();
    const [loading, setLoading] = useState<boolean>(false);
    const [tollAreas, setTollAreas] = useState<TollAreaModel[]>([]);
    const defaultFilter = GlobalUtils.getPreviousMonthFilter();
    const [month, setMonth] = useState<string>(defaultFilter.month.value);
    const [year, setYear] = useState<string>(defaultFilter.year.value);
    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        const date = dayjs(new Date(parseInt(data.year), parseInt(data.month) - 1));
        setMonth(data.month);
        setYear(data.year);
        form.setFieldsValue({
            date: date,
            tollAreaId: data.tollAreaId
        });
    }
    useEffect(() => {
        handleDataChange();
    }, [data])

    const populateTollAreas = async () => {
        const _tollAreas = await tollAreaRepo.getAll();
        setTollAreas(_tollAreas);
    }

    // Initialize select options data
    const init = async () => {
        await populateTollAreas();
    }
    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const parseDateToMonthYear = (date: Dayjs) => {
        const month = (date.month() + 1).toString();
        const year = date.year().toString();
        return {month, year};
    }

    const handleSave = async () => {
        try {
            setLoading(true);
            const {approveStatus, tollAreaId} = await form.validateFields();
            const result = await waterInvoiceRepo.approveByTollArea({
                month, year, approveStatus, tollAreaId
            });
             if (result === true) {
                message.success({
                    content: `Sửa trạng thái duyệt hóa đơn tháng ${month}/${year} thành công!`
                })
                setIsModalOpen(false);
            } else {
                message.error({
                    content: typeof result === "string"?result:`Sửa trạng thái duyệt hóa đơn tháng ${month}/${year} thất bại`
                })
            }
        } catch (error) {
            message.error({
                content: `Sửa trạng thái duyệt hóa đơn tháng ${month}/${year} thất bại`
            })
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal open={isModalOpen} onCancel={handleCancel} width={510} centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <Popconfirm
                            title={`Sửa trạng thái duyệt?`}
                            description={`Bạn có chắc chắn muốn sửa trạng thái duyệt hoá đơn tháng ${month}/${year}?`}
                            onConfirm={handleSave}
                            okText="Sửa hóa đơn"
                            cancelText="Hủy"
                            okButtonProps={{style: {backgroundColor: "var(--primary-color)"}}}
                            icon={<ExclamationCircleFilled style={{color: "var(--danger-color)"}} />}
                        >
                            <Button key="submit" type="primary" loading={loading}
                                style={{ 
                                    background: "var(--primary-color)", 
                                    paddingRight: 48, 
                                    paddingLeft: 48, 
                                    fontSize: 18,
                                    fontWeight: 600,
                                    paddingTop: 18, 
                                    paddingBottom: 18,
                                    }}
                            >
                                SỬA TRẠNG THÁI DUYỆT
                            </Button>
                        </Popconfirm>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">SỬA TRẠNG THÁI DUYỆT</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<_ApproveByTollAreaQuery>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                    >
                        <Form.Item<_ApproveByTollAreaQuery>
                            label="Tháng"
                            name="date"
                            getValueProps={(i) => ({ value: dayjsPipe(i) })}
                        >
                            <DatePicker.MonthPicker
                                format={DEF_MONTH_FORMAT}
                                onChange={(date) => {
                                    const {month, year} = parseDateToMonthYear(date);
                                    setMonth(month);
                                    setYear(year);
                                }}
                                style={{width: INPUT_WIDTH}}
                            />
                        </Form.Item>
                        <Form.Item<_ApproveByTollAreaQuery>
                            label="Khu vực"
                            name="tollAreaId"
                            rules={[{required: true, message: "Trường này là bắt buộc!"}]}
                        >
                            <Select style={{width: INPUT_WIDTH}} showSearch allowClear 
                            options={[
                                {value: undefined, label: 'Tất cả'},
                                ...tollAreas.map((tollArea, index) => ({value: tollArea.id, label: tollArea.name}))
                            ]}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                            }
                            />
                        </Form.Item>
                        <Form.Item<_ApproveByTollAreaQuery>
                            label="Trạng thái duyệt"
                            name="approveStatus"
                            rules={[{required: true, message: "Trường này là bắt buộc!"}]}
                        >
                            <Select showSearch allowClear style={{width: INPUT_WIDTH}}
                            options={[
                                {value: undefined, label: 'Tất cả'},
                                ...Object.keys(WaterInvoiceApproveStatus).filter(k => k !== WaterInvoiceApproveStatus.All).map((status, index) => ({value: status, label: waterInvoiceApproveStatusPipe(status)}))
                            ]}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                            }
                            />
                        </Form.Item>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}